import type { TFunction } from 'i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type { AnalyzedQueryInfo } from '@/stores/query-results.store';
import type { ReasoningPhase } from './reasoning-phase';

const Q = I18nNamespace.QUERIES;
const B = 'reasoningBlurbs';

export interface ReasoningBlurbContext {
    query: string | null;
    hasAttachment: boolean;
    sourceCount: number;
    timeRangeLabel: string | null;
    confidenceMin: number | null;
    analyzed: AnalyzedQueryInfo | null;
}

export function buildReasoningBlurbs(
    t: TFunction,
    phase: ReasoningPhase,
    blurbContext: ReasoningBlurbContext,
): string[] {
    if (!phase) return [];

    const query = blurbContext.query?.trim() ?? '';
    const hasText = query.length > 0;
    const { hasAttachment, sourceCount, timeRangeLabel, confidenceMin } =
        blurbContext;
    const analyzed = blurbContext.analyzed;
    const interpolate = (text: string, opts?: Record<string, unknown>) =>
        opts
            ? text.replace(/\{\{(\w+)\}\}/g, (_, name) =>
                  opts[name] !== undefined ? String(opts[name]) : `{{${name}}}`,
              )
            : text;
    const tr = (key: string, opts?: Record<string, unknown>) => {
        const variants = t(`${B}.${key}`, { ns: Q, returnObjects: true }) as
            | string
            | string[];
        const pool = Array.isArray(variants) ? variants : [variants];
        const chosen = pool[Math.floor(Math.random() * pool.length)] ?? '';
        return interpolate(chosen, opts);
    };

    const lines: string[] = [];

    if (phase === 'analyzing') {
        if (hasAttachment && hasText) {
            lines.push(tr('analyzingCombined', { query }));
        } else if (hasAttachment) {
            lines.push(tr('analyzingAttachment'));
            lines.push(tr('analyzingAttachmentDetails'));
        } else if (hasText) {
            lines.push(tr('analyzingText', { query }));
            lines.push(tr('analyzingTextDetails', { query }));
        }
        lines.push(tr('analyzingGeneric'));
        return lines;
    }

    if (phase === 'searching') {
        const sourceLabel = t('reasoning.source', {
            ns: Q,
            count: sourceCount,
        });
        if (sourceCount > 0 && timeRangeLabel) {
            lines.push(
                tr('searchingSourcesTime', {
                    count: sourceCount,
                    sourceLabel,
                    range: timeRangeLabel,
                }),
            );
        } else if (sourceCount > 0) {
            lines.push(
                tr('searchingSources', { count: sourceCount, sourceLabel }),
            );
        } else if (timeRangeLabel) {
            lines.push(tr('searchingTime', { range: timeRangeLabel }));
        }

        if (analyzed?.licensePlate) {
            lines.push(tr('searchingPlate', { plate: analyzed.licensePlate }));
        }
        if (analyzed?.subject) {
            lines.push(tr('searchingSubject', { subject: analyzed.subject }));
        }
        if (analyzed?.appearance) {
            lines.push(
                tr('searchingAppearance', { appearance: analyzed.appearance }),
            );
        }
        if (analyzed?.activity) {
            lines.push(
                tr('searchingActivity', { activity: analyzed.activity }),
            );
        }
        if (hasText) {
            lines.push(tr('searchingQuery', { query }));
        }
        if (hasAttachment) {
            lines.push(tr('searchingAttachment'));
        }
        if (confidenceMin != null) {
            lines.push(
                tr('searchingConfidence', { confidence: confidenceMin }),
            );
        }
        lines.push(tr('searchingRanking'));
        lines.push(tr('searchingGeneric'));
        return lines;
    }

    // displaying
    if (hasText) {
        lines.push(tr('displayingQuery', { query }));
    }
    lines.push(tr('displayingMore'));
    lines.push(tr('displayingRefining'));
    return lines;
}
